'use client';

import { DefaultSiteName } from '@/data/globals.data';
import { loadNaverMaps } from '@/lib/naver-maps';
import { NaverMap } from '@/types/map.type';
import { MarkerType } from '@/types/marker.type';
import { useCallback, useEffect, useRef, useState } from 'react';

// 지도 컨트롤 z-index 보정이 globals.css에서 이 id를 셀렉터로 쓰므로 유지한다.
export const MAP_ID = 'naver-map-container';

// 이 줌 레벨을 초과할 때만 마커에 지점명 라벨을 붙인다.
const LABEL_MIN_ZOOM = 12;

const isValidCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

interface Options {
  markers: MarkerType[];
  zoom: number;
}

/**
 * 네이버 지도를 만들고 마커 오버레이를 그리는 공통 훅.
 *
 * 반환한 containerRef를 지도를 그릴 요소에 연결하면 된다. SDK 로드는
 * loadNaverMaps가 담당하므로 마운트 시점과 무관하게 항상 로드 완료 후
 * 초기화된다.
 */
export function useNaverMap({ markers, zoom }: Options) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<NaverMap | null>(null);
  const overlaysRef = useRef<(naver.maps.OverlayView | null)[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const clearOverlays = useCallback(() => {
    overlaysRef.current.forEach((overlay) => {
      try {
        overlay?.setMap(null);
      } catch (error) {
        console.error('오버레이 정리 실패:', error);
      }
    });
    overlaysRef.current = [];
  }, []);

  const createCustomOverlay = useCallback(
    (map: NaverMap, { loc, title }: MarkerType, currentZoom: number) => {
      const [lng, lat] = loc ?? [];
      if (!isValidCoordinate(lng) || !isValidCoordinate(lat)) {
        console.error('마커 좌표가 올바르지 않습니다:', { lng, lat });
        return null;
      }

      try {
        const position = new window.naver.maps.LatLng(lat, lng);

        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.transform = 'translate(-50%, -100%)';
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.alignItems = 'center';

        const img = document.createElement('img');
        img.src = '/images/icons/icon_map_pin.svg';
        img.alt = title;
        const mediaQuery = window.matchMedia('(min-width: 500px)');
        const pinSize = mediaQuery.matches ? 40 : 30;
        img.width = pinSize;
        img.height = pinSize;
        img.style.display = 'block';
        container.appendChild(img);

        if (currentZoom > LABEL_MIN_ZOOM) {
          const label = document.createElement('div');
          label.innerHTML = `${DefaultSiteName}<br/>${title}`;
          label.style.fontSize = mediaQuery.matches ? '12px' : '10px';
          label.style.fontWeight = 'bold';
          label.style.lineHeight = 'normal';
          label.style.color = '#222222';
          label.style.textAlign = 'center';
          label.style.backgroundColor = '#ffffff';
          label.style.borderRadius = '100px';
          label.style.padding = '6px 16px';
          label.style.borderWidth = '1px';
          label.style.borderColor = '#E8ECF4';
          container.appendChild(label);
        }

        class CustomOverlay extends window.naver.maps.OverlayView {
          position: naver.maps.LatLng;
          element: HTMLElement;

          constructor(position: naver.maps.LatLng, element: HTMLElement) {
            super();
            this.position = position;
            this.element = element;
            this.setMap(map);
          }

          onAdd() {
            this.getPanes()?.overlayImage?.appendChild(this.element);
          }

          draw() {
            const point = this.getProjection()?.fromCoordToOffset(
              this.position,
            );
            if (!point) return;
            this.element.style.left = `${point.x}px`;
            this.element.style.top = `${point.y}px`;
          }

          onRemove() {
            this.element.remove();
          }
        }

        return new CustomOverlay(position, container);
      } catch (error) {
        console.error('오버레이 생성 실패:', error);
        return null;
      }
    },
    [],
  );

  const initialize = useCallback(() => {
    if (!isLoaded || !window.naver?.maps) return;
    if (!markers?.length) return;

    const container = containerRef.current;
    if (!container) return;

    try {
      // 기존 지도 정리
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
      clearOverlays();

      const [lng0, lat0] = markers[0].loc ?? [];
      if (!isValidCoordinate(lng0) || !isValidCoordinate(lat0)) {
        console.error('지도 중심 좌표가 올바르지 않습니다:', { lng0, lat0 });
        return;
      }

      const map = new window.naver.maps.Map(container, {
        center: new window.naver.maps.LatLng(lat0, lng0),
        zoom,
        scaleControl: true,
        mapDataControl: true,
        logoControl: true,
      });
      mapRef.current = map;

      const renderOverlays = (currentZoom: number) => {
        clearOverlays();
        markers.forEach((marker) => {
          overlaysRef.current.push(
            createCustomOverlay(map, marker, currentZoom),
          );
        });
      };

      // 지도 이동 시에는 오버레이 pane이 지도와 함께 움직이고, 확대/축소 시에는
      // draw()가 자동 호출돼 위치가 갱신된다. 따라서 오버레이를 다시 만들어야
      // 하는 경우는 라벨 노출 여부가 실제로 바뀔 때뿐이다.
      let isLabelVisible = zoom > LABEL_MIN_ZOOM;

      const handleZoomChange = (currentZoom: number) => {
        const nextLabelVisible = currentZoom > LABEL_MIN_ZOOM;
        if (nextLabelVisible === isLabelVisible) return;
        isLabelVisible = nextLabelVisible;
        renderOverlays(currentZoom);
      };

      window.naver.maps.Event.addListener(
        map,
        'zoom_changed',
        handleZoomChange,
      );

      renderOverlays(zoom);

      return () => {
        try {
          window.naver.maps.Event.clearListeners(map, 'zoom_changed');
        } catch (error) {
          console.error('지도 리스너 정리 실패:', error);
        }
        clearOverlays();
      };
    } catch (error) {
      console.error('지도 초기화 실패:', error);
    }
  }, [markers, zoom, isLoaded, clearOverlays, createCustomOverlay]);

  // 마운트 시점과 무관하게 SDK 로드 완료를 기다린다.
  useEffect(() => {
    const clientId = process.env.NCP_CLIENT_ID;
    if (!clientId) return;

    let cancelled = false;
    loadNaverMaps(clientId)
      .then(() => {
        if (!cancelled) setIsLoaded(true);
      })
      .catch((error) => {
        console.error(error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cleanup = initialize();
    return () => {
      cleanup?.();
    };
  }, [initialize]);

  return { containerRef, isLoaded };
}
